import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

function DownloadHelpPage() {
  const [activeStep, setActiveStep] = useState(null);

  const downloadSteps = [
    {
      id: 1,
      title: 'After Purchase Confirmation',
      description: 'What happens immediately after you complete your purchase',
      details: [
        'You\'ll receive an order confirmation email within 5 minutes',
        'Check your spam/junk folder if you don\'t see it in your inbox',
        'The email contains your order number and download links',
        'Links are valid for 30 days and can be used multiple times'
      ],
      icon: ''
    },
    {
      id: 2,
      title: 'Accessing Your Downloads',
      description: 'Multiple ways to access your purchased products',
      details: [
        'Click the download links in your confirmation email',
        'Log into your account dashboard at espanolhub.com/account',
        'Use the "My Products" section to re-download anytime',
        'Bookmark your account page for easy future access'
      ],
      icon: ''
    },
    {
      id: 3,
      title: 'Download Process',
      description: 'Step-by-step download instructions',
      details: [
        'Click on the product download link',
        'Choose your preferred file format (PDF, MP3, MP4, etc.)',
        'Select download location on your device',
        'Wait for download to complete (file sizes vary from 50MB to 2GB)'
      ],
      icon: ''
    },
    {
      id: 4,
      title: 'File Organization',
      description: 'How to organize your downloaded materials',
      details: [
        'Create a dedicated "Spanish Learning" folder',
        'Organize by product type (Audio, Video, PDFs, Exercises)',
        'Keep original file names for easy identification',
        'Back up your files to cloud storage or external drive'
      ],
      icon: '📁'
    }
  ];

  const troubleshooting = [
    {
      problem: 'Download link not working',
      solutions: [
        'Check if the link has expired (valid for 30 days)',
        'Try copying and pasting the link in a new browser tab',
        'Clear your browser cache and cookies',
        'Try using a different browser or incognito mode',
        'Contact support for a fresh download link'
      ]
    },
    {
      problem: 'Download is very slow',
      solutions: [
        'Check your internet connection speed',
        'Pause other downloads or streaming services',
        'Try downloading during off-peak hours',
        'Use a download manager for large files',
        'Switch to a wired connection if using WiFi'
      ]
    },
    {
      problem: 'File won\'t open or is corrupted',
      solutions: [
        'Re-download the file completely',
        'Check if you have the required software (PDF reader, media player)',
        'Try opening on a different device',
        'Scan the file with antivirus software',
        'Contact support if the file is consistently corrupted'
      ]
    },
    {
      problem: 'Can\'t find my download email',
      solutions: [
        'Check your spam/junk/promotions folder',
        'Search for emails from "noreply@espanolhub.com"',
        'Add our email to your contacts/safe senders list',
        'Check if you used the correct email address during purchase',
        'Contact support with your order number for resend'
      ]
    }
  ];

  const deviceInstructions = [
    {
      device: 'Windows PC',
      icon: '',
      steps: [
        'Right-click the download link and select "Save link as"',
        'Choose your desired download location',
        'Wait for download to complete',
        'Double-click the file to open or extract if it\'s a ZIP file'
      ]
    },
    {
      device: 'Mac',
      icon: '',
      steps: [
        'Control-click the download link and select "Download Linked File"',
        'Files typically download to your Downloads folder',
        'Double-click to open or use Archive Utility for ZIP files',
        'Move files to your preferred location'
      ]
    },
    {
      device: 'iPhone/iPad',
      icon: '',
      steps: [
        'Tap and hold the download link',
        'Select "Download Linked File"',
        'Files save to the Files app in Downloads folder',
        'Use appropriate apps to open different file types'
      ]
    },
    {
      device: 'Android',
      icon: '',
      steps: [
        'Tap the download link',
        'Choose "Download" when prompted',
        'Files save to your Downloads folder',
        'Use a file manager app to organize your downloads'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Download Help Center
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Complete guide to downloading and accessing your Spanish learning materials
          </p>
        </div>
      </section>

      {/* Quick Access */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition">
              <h3 className="text-xl font-semibold mb-2">Access My Downloads</h3>
              <p className="text-gray-600 mb-4">
                Log into your account to access all your purchased products
              </p>
              <a href="#" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition">
                My Account
              </a>
            </div>
            <div className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition">
              <h3 className="text-xl font-semibold mb-2">Resend Download Email</h3>
              <p className="text-gray-600 mb-4">
                Lost your download email? We can send it again
              </p>
              <Link to="/contact" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition">
                Request Resend
              </Link>
            </div>
            <div className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition">
              <h3 className="text-xl font-semibold mb-2">Technical Support</h3>
              <p className="text-gray-600 mb-4">
                Having trouble? Our support team is here to help
              </p>
              <Link to="/contact" className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition">
                Get Help
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Download Process Steps */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Download Process Guide
            </h2>
            <p className="text-lg text-gray-600">
              Follow these steps to successfully download your Spanish learning materials
            </p>
          </div>

          <div className="space-y-6">
            {downloadSteps.map((step) => (
              <div key={step.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => setActiveStep(activeStep === step.id ? null : step.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition"
                >
                  <div className="flex items-center">
                    <span className="text-3xl mr-4">{step.icon}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Step {step.id}: {step.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      activeStep === step.id ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {activeStep === step.id && (
                  <div className="px-6 pb-4 border-t border-gray-100">
                    <ul className="mt-4 space-y-2">
                      {step.details.map((detail, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-green-600 mr-3 mt-1">•</span>
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Device-Specific Instructions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Device-Specific Instructions
            </h2>
            <p className="text-lg text-gray-600">
              Download instructions for your specific device
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {deviceInstructions.map((device, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <div className="text-center mb-4">
                  <span className="text-4xl">{device.icon}</span>
                  <h3 className="text-lg font-semibold mt-2">{device.device}</h3>
                </div>
                <ol className="space-y-2">
                  {device.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="text-sm text-gray-700 flex items-start">
                      <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">
                        {stepIndex + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Troubleshooting Common Issues
            </h2>
            <p className="text-lg text-gray-600">
              Solutions to the most common download problems
            </p>
          </div>

          <div className="space-y-6">
            {troubleshooting.map((item, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="text-red-500 mr-3">Issue:</span>
                  {item.problem}
                </h3>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-3">Solutions:</h4>
                  <ul className="space-y-2">
                    {item.solutions.map((solution, solutionIndex) => (
                      <li key={solutionIndex} className="flex items-start">
                        <span className="text-blue-600 mr-3 mt-1">•</span>
                        <span className="text-blue-800">{solution}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* File Types Guide */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              File Types & Requirements
            </h2>
            <p className="text-lg text-gray-600">
              What you need to open different types of files
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold mb-2">PDF Files</h3>
              <p className="text-gray-600 text-sm mb-3">
                Ebooks, worksheets, guides
              </p>
              <p className="text-xs text-gray-500">
                Requires: Adobe Reader, browser, or PDF viewer
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold mb-2">Audio Files</h3>
              <p className="text-gray-600 text-sm mb-3">
                MP3 lessons, music, pronunciation
              </p>
              <p className="text-xs text-gray-500">
                Requires: Any media player (built-in on most devices)
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold mb-2">Video Files</h3>
              <p className="text-gray-600 text-sm mb-3">
                MP4 lessons, cultural content
              </p>
              <p className="text-xs text-gray-500">
                Requires: VLC, QuickTime, or any video player
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold mb-2">Mobile Apps</h3>
              <p className="text-gray-600 text-sm mb-3">
                APK (Android), IPA (iOS)
              </p>
              <p className="text-xs text-gray-500">
                Install instructions included with download
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold mb-2">ZIP Archives</h3>
              <p className="text-gray-600 text-sm mb-3">
                Complete course packages
              </p>
              <p className="text-xs text-gray-500">
                Extract using built-in tools or WinRAR/7-Zip
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold mb-2">Interactive Content</h3>
              <p className="text-gray-600 text-sm mb-3">
                HTML5 games, quizzes
              </p>
              <p className="text-xs text-gray-500">
                Open in any modern web browser
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Still Need Help?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Our support team is available to help you with any download issues
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              Contact Support
            </Link>
            <Link
              to="/free-ebook"
              className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition"
            >
              FREE EBOOK
            </Link>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Average response time: Under 2 hours
          </p>
        </div>
      </section>

      {/* Shared Footer */}
      <Footer />
    </div>
  );
}

export default DownloadHelpPage;